import React, { useCallback, useContext, useMemo } from "react";
import { map } from "lodash";
import { RESOURCE_NAME } from "common/authorization";
import {
  COMMENT_OBJECT_TYPE,
  FEATURE_FLAG_NAME,
  SUBMISSION_TAB,
} from "common/constants";
import { generateDefaultValuesObject } from "common/utils/defaultValues";
import {
  GetPropertyForSubmissionQuery,
  GetSisdRuleDataQuery,
  GetSubmissionQuery,
} from "../../generated/graphql";
import useNavigationTab from "../../hooks/useNavigationTabs";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { TabGroupContainer } from "../Common/__styles__/TabGroup";
import TabGroup from "../Common/TabGroup";
import { Thread } from "../Threads/Thread";
import { FormComponent, FormDataType } from "./Form";

export const Content = ({
  submission,
  regeneratingPaths,
  handleRegenerateAIFields,
  setFormDisabled,
  propertyProp,
  property,
  serverProvidedFormData,
  onSubmit,
  account,
  submitFormRef,
}: {
  submission: NonNullable<GetSubmissionQuery["submission"]>;
  regeneratingPaths: Record<string, boolean>;
  handleRegenerateAIFields: (
    formData: FormDataType,
    paths: string[],
  ) => Promise<void>;
  setFormDisabled: (disabled: boolean) => void;
  propertyProp: {
    FIRMInfo?: GetSisdRuleDataQuery["FIRMInfo"];
  };
  property: GetPropertyForSubmissionQuery["property"];
  serverProvidedFormData: FormDataType | null;
  onSubmit: (formData: FormDataType) => Promise<void>;
  account: GetSisdRuleDataQuery["account"];
  submitFormRef: React.RefObject<HTMLButtonElement>;
}) => {
  const { user, admin, publicUser, authorized, isFeatureEnabled } =
    useContext(AuthContext);
  const currentUser = user ?? admin ?? publicUser;
  const submissionCommentsEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.SUBMISSION_COMMENTS,
  );

  // TODO: Allow specific public users to view comment thread
  const canViewCommentThread =
    authorized({
      resource: RESOURCE_NAME.COMMENT,
      permission: "list",
    }) && currentUser?.__typename !== "PublicUser";

  const tabs = useMemo(
    () => [
      { label: "Form", value: SUBMISSION_TAB.FORM },
      ...(submissionCommentsEnabled && canViewCommentThread ?
        [{ label: "Comments", value: SUBMISSION_TAB.COMMENTS }]
      : []),
    ],
    [submissionCommentsEnabled, canViewCommentThread],
  );

  const allowedTabs = useMemo(() => map(tabs, "value"), [tabs]);
  const defaultTab = useMemo(() => tabs[0]!.value, [tabs]);

  const [tab, setTab] = useNavigationTab({
    defaultTab,
    allowedTabs,
  });

  const dynamicDefaultValues = useMemo(() => {
    return generateDefaultValuesObject({
      property,
      user: user ?? admin,
    });
  }, [property, user, admin]);

  const handleOnChange = useCallback(
    ({
      isDirty,
      documentUploadsInProgress,
    }: {
      isDirty: boolean;
      documentUploadsInProgress: boolean;
    }) => {
      setFormDisabled(!isDirty || documentUploadsInProgress);
    },
    [setFormDisabled],
  );

  const canUpdateSubmission = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "update",
  });

  return (
    <>
      {submissionCommentsEnabled && canViewCommentThread && (
        <TabGroupContainer style={{ margin: `${spacing.xl} 0` }}>
          <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
        </TabGroupContainer>
      )}
      {tab === "form" && (
        <FormComponent
          readonly={!canUpdateSubmission}
          submissionType={{
            currentVersion: submission.submissionTypeVersion,
            modules: submission.submissionTypeVersion.submissionType.modules,
            id: submission.submissionTypeVersion.submissionType.id,
          }}
          existingSubmission={submission}
          relatedSubmissions={submission.relatedSubmissions}
          onSubmit={onSubmit}
          onChange={handleOnChange}
          submitFormRef={submitFormRef}
          dynamicDefaultValues={dynamicDefaultValues}
          account={account}
          property={propertyProp}
          loadingFormPaths={regeneratingPaths}
          regenerateAIFields={handleRegenerateAIFields}
          serverProvidedFormData={serverProvidedFormData ?? undefined}
        />
      )}
      {tab === "comments" && (
        <Thread
          objectId={submission.id}
          objectType={COMMENT_OBJECT_TYPE.SUBMISSION}
        />
      )}
    </>
  );
};
